import { create } from 'zustand';
import { CustomizationStore, CustomizationState } from './types';

const initialState: CustomizationState = {
  backgroundColor: 'transparent',
  transparentBackground: false,
  activeTextId: 'initial-text',
  activeImageId: null,
  textElements: [{
    id: 'initial-text',
    text: 'Your text',
    color: '#000000',
    size: 15,
    rotation: 0,
    font: 'Bree Serif',
    position: { left: 54, top: 65 },
  }],
  imageElements: [],
  canvasWidth: 108,
  canvasHeight: 160,
};

export const useStore = create<CustomizationStore>((set, get) => ({
  ...initialState,
  originalDesign: initialState,

  setBackgroundColor: (color) => set({ backgroundColor: color }),
  setActiveTextId: (id) => set({ activeTextId: id }),
  setActiveImageId: (id) => set({ activeImageId: id }),
  setCanvasDimensions: (width: number, height: number) => 
    set({ canvasWidth: width, canvasHeight: height }),

  addTextElement: () => {
    const state = get();
    const lastElement = state.textElements[state.textElements.length - 1];
    
    // Use the size from the last element, or default to 15 if no elements exist
    const size = lastElement?.size || 15;
    
    // Calculate center position based on canvas dimensions
    let left = state.canvasWidth / 2;
    let top = state.canvasHeight / 2;
    
    // If there are existing elements, offset the new element slightly below the last one
    if (lastElement) {
      const offset = size * 1.5; // Offset by 1.5 times the font size
      const newTop = Math.min(lastElement.position.top + offset, state.canvasHeight - size);
      if (newTop + size <= state.canvasHeight) {
        top = newTop;
      }
    }

    const id = Date.now().toString();
    set((state) => ({
      textElements: [
        ...state.textElements,
        {
          id,
          text: 'Nouveau texte',
          color: '#000000',
          size,
          rotation: 0,
          font: 'Bree Serif',
          position: { left, top },
        },
      ],
      activeTextId: id,
    }));
    return id;
  },

  addImageElement: (url) => set((state) => ({
    imageElements: [...state.imageElements, { id: Date.now().toString(), url }],
  })),

  removeImageElement: (id) => set((state) => ({
    imageElements: state.imageElements.filter((img) => img.id !== id),
    activeImageId: null,
  })),

  updateTextElement: (id, updates) => set((state) => ({
    textElements: state.textElements.map((element) =>
      element.id === id ? { ...element, ...updates } : element
    ),
  })),

  importDesign: (design) => {
    // Ensure the design has all required properties
    const validDesign = {
      ...initialState,
      ...design,
      // Ensure at least one text element exists
      textElements: design.textElements?.length ? design.textElements : initialState.textElements,
    };
    
    set({
      ...validDesign,
      originalDesign: validDesign,
      // Set active text ID to the first text element
      activeTextId: validDesign.textElements[0]?.id || null,
    });
  },
  
  exportDesign: () => ({
    backgroundColor: get().backgroundColor,
    transparentBackground: get().transparentBackground,
    activeTextId: get().activeTextId,
    activeImageId: get().activeImageId,
    textElements: get().textElements,
    imageElements: get().imageElements,
    canvasWidth: get().canvasWidth,
    canvasHeight: get().canvasHeight,
  }),

  resetDesign: () => {
    const original = get().originalDesign;
    set({ ...original });
  },
}));